import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StreamOutput - Clean output view for OBS, vMix, streaming software
 * 
 * URL Parameters:
 * - /stream/:projectId - Basic stream output
 * - ?auto=true - Enable auto-advance
 * - ?speed=5 - Seconds between slides (default 5)
 * - ?transition=fade - Transition type (fade, slide, none)
 * - ?bg=transparent - Transparent background for overlay
 */
const StreamOutput = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);
  
  // URL parameters
  const autoPlay = searchParams.get('auto') === 'true';
  const speed = parseInt(searchParams.get('speed') || '5', 10);
  const transition = searchParams.get('transition') || 'fade';
  const transparentBg = searchParams.get('bg') === 'transparent';

  const fetchProject = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (err) {
      setError("Project not found");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
    // Poll for updates every 2 seconds (for remote control)
    const pollInterval = setInterval(fetchProject, 2000);
    return () => clearInterval(pollInterval);
  }, [fetchProject]);

  // Auto-advance slides
  useEffect(() => {
    if (autoPlay && project?.slides?.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => 
          prev >= project.slides.length - 1 ? 0 : prev + 1
        );
      }, speed * 1000);
      return () => clearInterval(interval);
    }
  }, [autoPlay, speed, project?.slides?.length]);

  // Keyboard controls (for testing)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        setCurrentIndex(prev => 
          prev >= (project?.slides?.length || 1) - 1 ? prev : prev + 1
        );
      } else if (e.key === "ArrowLeft") {
        setCurrentIndex(prev => prev > 0 ? prev - 1 : 0);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [project?.slides?.length]);

  // Listen for external control messages (postMessage API)
  useEffect(() => {
    const handleMessage = (event) => {
      const { action, slideIndex } = event.data || {};
      if (action === 'next') {
        setCurrentIndex(prev => 
          prev >= (project?.slides?.length || 1) - 1 ? prev : prev + 1
        );
      } else if (action === 'prev') {
        setCurrentIndex(prev => prev > 0 ? prev - 1 : 0);
      } else if (action === 'goto' && typeof slideIndex === 'number') {
        setCurrentIndex(slideIndex);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [project?.slides?.length]);

  if (loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-black">
        <div className="text-white animate-pulse">Loading...</div>
      </div>
    );
  }

  if (error || !project?.slides?.length) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-black text-white">
        {error || "No slides available"}
      </div>
    );
  }

  const currentSlide = project.slides[currentIndex];
  
  // Get background style
  const getBackgroundStyle = () => {
    if (transparentBg) {
      return { backgroundColor: 'transparent' };
    }
    if (currentSlide.background_type === 'gradient' && currentSlide.background_image) {
      return { background: currentSlide.background_image };
    }
    return { backgroundColor: currentSlide.background_color || '#000000' };
  };

  // Transition variants
  const transitionVariants = {
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.5 }
    },
    slide: {
      initial: { opacity: 0, x: 100 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -100 },
      transition: { duration: 0.3 }
    },
    none: {
      initial: {},
      animate: {},
      exit: {},
      transition: { duration: 0 }
    }
  };

  const variant = transitionVariants[transition] || transitionVariants.fade;

  return (
    <div 
      className="w-screen h-screen overflow-hidden"
      style={transparentBg ? { backgroundColor: 'transparent' } : {}}
      data-testid="stream-output"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={variant.initial}
          animate={variant.animate}
          exit={variant.exit}
          transition={variant.transition}
          className="w-full h-full flex items-center justify-center relative"
          style={{
            ...getBackgroundStyle(),
            color: currentSlide.text_color || '#ffffff'
          }}
        >
          {/* Background Image */}
          {currentSlide.background_type === 'image' && currentSlide.background_image && !transparentBg && (
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${currentSlide.background_image})` }}
            />
          )}
          
          {/* Background GIF */}
          {currentSlide.background_type === 'gif' && currentSlide.background_image && !transparentBg && (
            <img 
              src={currentSlide.background_image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          
          {/* Overlay for readability */}
          {(currentSlide.background_type === 'image' || currentSlide.background_type === 'gif') && currentSlide.background_image && !transparentBg && (
            <div className="absolute inset-0 bg-black/30" />
          )}

          {/* Content */}
          <div className="relative z-10 max-w-[90%] max-h-[85vh] text-center px-8 overflow-hidden">
            {/* Type Badge (optional, can be hidden via CSS if needed) */}
            {currentSlide.type !== 'text' && currentSlide.type !== 'title' && (
              <div className="mb-6">
                <span 
                  className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${
                    currentSlide.type === 'chorus' 
                      ? 'bg-amber-500 text-black' 
                      : 'bg-white/20 text-white'
                  }`}
                >
                  {currentSlide.type.toUpperCase()}
                </span>
              </div>
            )}
            
            {/* Main Text */}
            <p 
              className="font-lyrics leading-relaxed whitespace-pre-line overflow-hidden"
              style={{ 
                fontSize: `clamp(28px, ${currentSlide.font_size / 10}vw, ${currentSlide.font_size * 1.5}px)`,
                textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
                letterSpacing: '0.02em',
                maxHeight: '75vh',
                display: '-webkit-box',
                WebkitLineClamp: 12,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {currentSlide.content}
            </p>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default StreamOutput;
